import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  Divider,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import CloseButton from 'components/CustomButtons/CloseButton';
import {
  getCustomerSearchResultsConfiguration,
  getCustomerSearchResultsSortingConfiguration,
} from 'reducers/configs/settings';
import { SaveButton } from 'components/UIElements/UIButton';
import {
  saveTempSetting,
  commitTempSettings,
  dropTempSettings,
} from 'actions/configs';
import { useAppDispatch } from 'reducers';
import { CustomerSortingCriteria } from 'reducers/configs/types';
import { getUseCustomerRegistry } from 'reducers/customerSearch';

import { Checkbox, Section } from '../../components/Inputs';

const CONFIG_NAME = 'customer_search_results_configuration';
const SORTING_CONFIG_NAME = 'customer_search_results_sorting_configuration';

const SORTING_DIRECTIONS = ['asc', 'desc'];

const SORTING_CRITERIA: CustomerSortingCriteria[] = [
  'name',
  'customerID',
  'group',
  'colorStatus',
  'lastChanged',
  'homeStoreID',
  'signUpStoreID',
];

function CustomerSearchResults({ onClose: goToPreviousModalPage }) {
  const dispatch = useAppDispatch();

  const { t } = useTranslation('settingsCustomerSearchResults');

  const onClose = () => {
    dispatch(dropTempSettings([CONFIG_NAME, SORTING_CONFIG_NAME]));
    goToPreviousModalPage();
  };

  const usingCustomerRegistry = useSelector(getUseCustomerRegistry);

  const config = useSelector(getCustomerSearchResultsConfiguration);
  const sortingConf = useSelector(getCustomerSearchResultsSortingConfiguration);

  const set = <
    TKey extends keyof typeof config,
    TValue extends typeof config[TKey]
  >(
    key: TKey,
    value: TValue,
  ) => {
    dispatch(
      saveTempSetting(
        CONFIG_NAME,
        JSON.stringify({
          ...config,
          [key]: value ? 1 : 0,
        }),
      ),
    );
  };

  const onSortEdit = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string,
  ) => {
    let key;
    let value;
    // Checkbox's event type is `string` and not ChangeEvent
    if (typeof e === 'string') {
      key = e;
      value = !sortingConf[e];
    } else {
      key = e.target.name;
      value = e.target.value;
    }
    dispatch(
      saveTempSetting(
        SORTING_CONFIG_NAME,
        JSON.stringify({
          ...sortingConf,
          [key]: value,
        }),
      ),
    );
  };

  const onSave = () => {
    dispatch(commitTempSettings([CONFIG_NAME, SORTING_CONFIG_NAME])).then(
      onClose,
    );
  };

  return (
    <>
      <Box
        padding="1.5rem"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5">{t('title')}</Typography>
        <Box display="flex" alignItems="center">
          <SaveButton action={onSave} variant="POS" />
          <CloseButton action={onClose} />
        </Box>
      </Box>
      <Divider />
      <Box padding="1rem">
        <Typography variant="h5">{t('introduction')}</Typography>
        <Typography variant="h6">{t('instructions')}</Typography>
      </Box>
      <Box>
        <Section title={t('sections.normalGrid')}>
          <Checkbox onChange={set} name="customerID" value={config.customerID}>
            {t('customerID')}
          </Checkbox>
          <Checkbox onChange={set} name="name" value={config.name}>
            {t('name')}
          </Checkbox>
          <Checkbox onChange={set} name="email" value={config.email}>
            {t('email')}
          </Checkbox>
          <Checkbox onChange={set} name="phone" value={config.phone}>
            {t('phone')}
          </Checkbox>
          <Checkbox onChange={set} name="mobile" value={config.mobile}>
            {t('mobile')}
          </Checkbox>
          <Checkbox onChange={set} name="postalCode" value={config.postalCode}>
            {t('postalCode')}
          </Checkbox>
          <Checkbox onChange={set} name="cardNr" value={config.cardNr}>
            {t('cardNr')}
          </Checkbox>
          <Checkbox onChange={set} name="address" value={config.address}>
            {t('address')}
          </Checkbox>
        </Section>

        <Section title={t('sections.retailGrid')}>
          <Checkbox
            onChange={set}
            name="retail_customerID"
            value={config.retail_customerID}
          >
            {t('customerID')}
          </Checkbox>
          <Checkbox
            onChange={set}
            name="retail_name"
            value={config.retail_name}
          >
            {t('name')}
          </Checkbox>
          <Checkbox
            onChange={set}
            name="retail_email"
            value={config.retail_email}
          >
            {t('email')}
          </Checkbox>
          <Checkbox
            onChange={set}
            name="retail_phone"
            value={config.retail_phone}
          >
            {t('phone')}
          </Checkbox>
          <Checkbox
            onChange={set}
            name="retail_mobile"
            value={config.retail_mobile}
          >
            {t('mobile')}
          </Checkbox>
          <Checkbox
            onChange={set}
            name="retail_postalCode"
            value={config.retail_postalCode}
          >
            {t('postalCode')}
          </Checkbox>
          <Checkbox
            onChange={set}
            name="retail_cardNr"
            value={config.retail_cardNr}
          >
            {t('cardNr')}
          </Checkbox>
          <Checkbox
            onChange={set}
            name="retail_address"
            value={config.retail_address}
          >
            {t('address')}
          </Checkbox>
        </Section>
      </Box>
      <Box padding="1rem">
        <Typography variant="h5">{t('sorting.title')}</Typography>
        {usingCustomerRegistry ? (
          <Typography variant="h6">
            {t('sorting.noteCustomerRegistry')}
          </Typography>
        ) : (
          <>
            <Checkbox
              value={sortingConf.enabled}
              name="enabled"
              onChange={onSortEdit}
            >
              {t('sorting.labels.enable')}
            </Checkbox>
            <Alert severity="info">{t('sorting.note')}</Alert>
            <Box
              display="flex"
              flexDirection="column"
              gridGap="1.5rem"
              marginTop="1.5rem"
            >
              <TextField
                select
                fullWidth
                disabled={!sortingConf.enabled}
                label={t('sorting.labels.direction')}
                size="small"
                variant="outlined"
                onChange={onSortEdit}
                value={sortingConf.direction}
                name="direction"
              >
                {SORTING_DIRECTIONS.map(dir => (
                  <MenuItem value={dir} key={`sort-direction-${dir}`}>
                    {t(`sorting.options.direction.${dir}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                disabled={!sortingConf.enabled}
                label={t('sorting.labels.criteria')}
                size="small"
                variant="outlined"
                onChange={onSortEdit}
                value={sortingConf.criteria}
                name="criteria"
              >
                {SORTING_CRITERIA.map(crit => (
                  <MenuItem value={crit} key={`sort-criteria-${crit}`}>
                    {t(`sorting.options.criteria.${crit}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}

export default CustomerSearchResults;
